import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import {
  borderRadius,
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import Image from 'presentations/views/components/atoms/Image';
import Link from 'presentations/views/components/atoms/Link';
import Icon from 'presentations/views/components/atoms/Icon';
import { black } from 'presentations/views/styles/color';
import { CustomerStoryListItem } from 'presentations/api/schema/contents';
import { customerStoryPath } from 'presentations/routes/path';
import { useEffect, useState } from 'react';
import { blendAlphaToHex } from 'presentations/views/helpers/color';
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

const renderSlide = (customer: CustomerStoryListItem) => {
  const t = translateInComponent(translations);
  return (
    <SlideListItem
      startColor={blendAlphaToHex(customer.customerColor, 0, 0.5)}
      endColor={blendAlphaToHex(customer.customerColor, 80, 0.7)}
      href={customerStoryPath(customer.slug)}
    >
      <SlideListItemMainContainer>
        <SlideListItemTitle>{customer.topLabel}</SlideListItemTitle>
        <SlideListItemText>{customer.title}</SlideListItemText>
      </SlideListItemMainContainer>
      <SlideListItemLinkContainer>
        {customer.companyWhiteImageUrl ? (
          <>
            <SlideListItemImageWrapper>
              <SlideListItemImage
                src={customer.companyWhiteImageUrl}
                alt={customer.customerName}
                width={100}
                height={16}
                dpr={2}
                loading="auto"
              />
            </SlideListItemImageWrapper>
            <SlideListItemSuffix>{t('Story')}</SlideListItemSuffix>
          </>
        ) : (
          <>
            <SlideListItemImageText>
              {customer.customerName}
            </SlideListItemImageText>
            <SlideListItemTextSuffix>{t('Story')}</SlideListItemTextSuffix>
          </>
        )}
        <SlideListItemSuffixIcon name="chevron-right" />
      </SlideListItemLinkContainer>
    </SlideListItem>
  );
};

const SlideShow = (props: { customers: CustomerStoryListItem[] }) => (
  <SlideList>
    {props.customers.map((customer) => (
      <Slide key={customer.slug}>{renderSlide(customer)}</Slide>
    ))}
  </SlideList>
);

interface Props {
  customers: CustomerStoryListItem[];
}

export default function Component(props: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  useEffect(() => {
    setIsLoading(false);
  }, []);
  if (isLoading) return null;
  return (
    <SliderWrapper>
      <SlideShow customers={props.customers} />
      <SlideShow customers={props.customers} />
      <SlideShow customers={props.customers} />
    </SliderWrapper>
  );
}
const SliderWrapper = styled.div`
  display: flex;
  align-items: center;
  /* overflow: hidden; */
`;
const SlideAnimation = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-100%);
  }
`;
const SlideList = styled.div`
  display: flex;
  animation: ${SlideAnimation} 60s infinite linear 1s both;
`;
const Slide = styled.div`
  margin-right: ${spacing(12)};
  ${mediaMax.smartphone} {
    margin-right: ${spacing(8)};
  }
`;
const SlideListItem = styled(Link)<{ startColor: string; endColor: string }>`
  width: 244px;
  height: 128px;
  border: 1px solid rgba(149, 163, 201, 0.29);
  background: ${(props) =>
    `linear-gradient(214deg, ${props.startColor} 0%, ${props.endColor} 100%)`};

  backdrop-filter: blur(15px) brightness(100%);
  border-radius: ${borderRadius(12)};
  padding: ${spacing(16)} ${spacing(20)};
  color: ${black};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-align: left;
  box-shadow: 0px 6px 8px 0px rgba(0, 0, 0, 0.16);
`;
const SlideListItemTitle = styled.div`
  font-size: ${fontSize(16)};
  line-height: 1.4;
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(8)};
`;
const SlideListItemText = styled.div`
  font-size: ${fontSize(12)};
  margin-bottom: ${spacing(4)};
  line-height: 1.4;
  max-height: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  /* white-space: nowrap; */
`;
const SlideListItemMainContainer = styled.div``;
const SlideListItemLinkContainer = styled.div`
  display: flex;
  align-items: center;
`;
const SlideListItemImageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  /* background-color: ${black}; */
  border-radius: ${borderRadius(3)};
  /* padding: ${spacing(2)} ${spacing(8)}; */
  margin-right: ${spacing(8)};
`;
const SlideListItemImage = styled(Image)<{ isWhiteImage?: boolean }>`
  max-width: 130px;
  width: fit-content;
  object-fit: contain;
  object-position: left;
  height: 20px;
  filter: ${(props) => !props.isWhiteImage && `brightness(0) invert(1)`};
`;
const SlideListItemImageText = styled.div`
  font-size: ${fontSize(14)};
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 130px;
  overflow: hidden;
  /* font-weight: ${fontWeight('bold')}; */
  text-decoration: underline;
`;
const SlideListItemSuffix = styled.div`
  font-size: ${fontSize(14)};
`;
const SlideListItemTextSuffix = styled(SlideListItemSuffix)`
  text-decoration: underline;
`;
const SlideListItemSuffixIcon = styled(Icon)`
  width: 18px !important;
  height: 18px !important;
  color: ${black};
`;
